/**
 * API Service Layer
 * 
 * Generic API service with common CRUD operations
 * Uses axios instance with interceptors for authentication and error handling
 */

import axiosInstance from '@/lib/axios';
import type { AxiosRequestConfig } from 'axios';

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  [key: string]: unknown; // Allow additional fields like warnings, replaced_goal_id, etc.
}

/**
 * Base API Service class with common methods
 */
class ApiService {
  /**
   * GET request
   */
  async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.get<ApiResponse<T>>(endpoint, config);
    
    // Return data field if exists, otherwise return whole response
    return (response.data as ApiResponse<T>)?.data ?? (response.data as T);
  }

  /**
   * GET request returning full response (with metadata)
   */
  async getFull<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.get<T>(endpoint, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T, D = unknown>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await axiosInstance.post<ApiResponse<T>>(endpoint, data, config);
    return (response.data as ApiResponse<T>)?.data ?? (response.data as T);
  }

  /**
   * POST request returning full response (with metadata)
   */
  async postFull<T, D = unknown>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await axiosInstance.post<T>(endpoint, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T, D = unknown>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await axiosInstance.put<ApiResponse<T>>(endpoint, data, config);
    return (response.data as ApiResponse<T>)?.data ?? (response.data as T);
  }

  /**
   * PATCH request
   */
  async patch<T, D = unknown>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await axiosInstance.patch<ApiResponse<T>>(endpoint, data, config);
    return (response.data as ApiResponse<T>)?.data ?? (response.data as T);
  }

  /**
   * PATCH request returning full response (with metadata)
   */
  async patchFull<T, D = unknown>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await axiosInstance.patch<T>(endpoint, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await axiosInstance.delete<ApiResponse<T>>(endpoint, config);
    
    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }
    
    return (response.data as ApiResponse<T>)?.data ?? (response.data as T);
  }
}

// Export singleton instance
export const apiService = new ApiService();

